import { useState } from 'react';
import './RejectionModal.css';

/**
 * RejectionModal - Admin-only modal dialog for rejecting a shipment.
 * 
 * Props:
 *   shipmentId - ID of the shipment being rejected
 *   onConfirm(reason) - Called with trimmed reason when admin confirms
 *   onCancel() - Called when admin cancels
 */
function RejectionModal({ shipmentId, onConfirm, onCancel }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isValid = reason.trim() !== '';

  const handleConfirm = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await onConfirm(reason.trim());
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to reject shipment');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content rejection-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Reject Shipment #{shipmentId}</h2>
          <button className="modal-close" onClick={onCancel} aria-label="Close">×</button>
        </div>

        <div className="rejection-modal-body">
          <p className="rejection-modal-description">
            Please provide a reason for rejecting this shipment. The requestor will be notified
            and can edit and resubmit the shipment.
          </p>

          {error && (
            <div className="error-message" style={{ margin: '10px 0' }}>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="rejection-reason">
              Rejection Reason <span className="required">*</span>
            </label>
            <textarea
              id="rejection-reason"
              className="rejection-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter the reason for rejection..."
              rows={4}
              disabled={submitting}
              aria-required="true"
            />
          </div>
        </div>

        <div className="modal-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={handleConfirm}
            disabled={!isValid || submitting}
          >
            {submitting ? 'Rejecting...' : 'Confirm Rejection'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RejectionModal;
